import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useDataRoomAccess } from '@/hooks/useDataRoomAccess';
import { useEntity } from '@/hooks/useEntity';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { ISSUE_REPORT_OFFER_STATE } from '@/lib/constants/issue-report';
import { ROUTES } from '@/lib/constants/routes';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { BILLING_STRINGS } from '../constants/billing-strings';
import { LIVE_SUBSCRIPTION_STATUSES } from '../constants/subscription-status';
import { useCheckoutCatalogue } from '../hooks/useCheckoutCatalogue';
import { PlanCatalogue } from './PlanCatalogue';

const COPY = BILLING_STRINGS.OVERVIEW;
const PURCHASE = BILLING_STRINGS.PURCHASE;

/**
 * The plans to choose from (FR-39; Sprint 7 QA, 2026-10-02).
 *
 * Only the plan card itself, with no outer card, title, intro or "Available
 * plan" heading: the client asked for the purchase to be 100% about the Issue
 * Report, with nothing around it that reads as data-room access or a choice
 * of plans (UX round 3, StartupAdminPatty X5/Y5).
 */
export function PlanChooser() {
  const catalogue = useCheckoutCatalogue();
  const entityId = useEntity()?.entityId ?? null;
  const access = useDataRoomAccess(entityId);
  const offer = useIssueReportOffer();

  if (catalogue.isLoading || access.isLoading) return <LoadingSpinner />;
  if (catalogue.isError || !catalogue.data) {
    return <ErrorState title={getApiErrorMessage(catalogue.error, PURCHASE.TITLE)} />;
  }

  const data = catalogue.data;
  // The Backend refuses a second purchase while a subscription is live, so
  // this says so up front instead of offering plans that 409 (Sprint 7 QA).
  const live =
    data.currentSubscription && LIVE_SUBSCRIPTION_STATUSES.includes(data.currentSubscription.status)
      ? data.currentSubscription
      : null;
  // SR NEXT Premium is access with no subscription behind it, so the
  // subscription check above cannot see it (A-109: buy only when nothing is
  // active). The access view can.
  const current = access.data?.current ?? null;
  const livePlanName = live
    ? data.plans.find((plan) => plan.id === live.planId)?.name
    : current
      ? COPY.SERVICE_LABELS[current.service]
      : undefined;
  const hasAccess = Boolean(live || current);

  return (
    <div className="flex flex-col gap-6">
      {hasAccess && (
        <div className="rounded-card bg-surface-raised mx-auto flex w-full max-w-2xl flex-wrap items-center justify-between gap-4 p-4" role="status">
          <p className="text-text-secondary text-sm">
            {PURCHASE.ALREADY_SUBSCRIBED.replace('{plan}', livePlanName ?? PURCHASE.PREMIUM_FALLBACK)}
          </p>
          <Button asChild variant="outline" className="inline-flex">
            <Link to={ROUTES.BILLING}>{PURCHASE.GO_TO_BILLING}</Link>
          </Button>
        </div>
      )}
      <PlanCatalogue
        catalogue={data}
        locked={hasAccess}
        issueReportComingSoon={offer.data?.state === ISSUE_REPORT_OFFER_STATE.COMING_SOON}
        // Loading or failed: no buy action for the Issue Report plan, so it
        // never flashes a buy button that then turns into "Coming Soon".
        issueReportOfferUnknown={!offer.data}
      />
    </div>
  );
}
